import React from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Box } from "@material-ui/core";
import { StaticLookupsIds } from "../../../../../assets/json/StaticLookupsIds";
import {
  GetAllCampaigns,
  GetAllDevelopersContactsAPI,
  getProperties,
  lookupItemsGetId,
  OrganizationUserSearch,
} from "../../../../../Services";
import {
  CustomAsyncAutocomplete,
  CustomCard,
  CustomCheckbox,
  CustomRangeSlider,
  CustomSwitch,
  ReskinningInputs,
  ReskinningRadioGroup,
} from "../../../../../Components";
import { useSelectedTheme, useTranslate } from "../../../../../Hooks";

import useStyles from "./styles";

function EditSchemaForm({ schemaDetails, formRef }) {
  const getValidationSchema = () => {
    return Yup.object({
      label: Yup.string().required(translate("error-rotation-name-required")),
      isActive: Yup.boolean(),
      order: Yup.number()
        .typeError(translate("error-order-is-number"))
        .required(translate("error-order-required"))
        .min(1, translate("error-order-bigger-than-1")),
      buyer: Yup.boolean(),
      tenant: Yup.boolean(),
      vip: Yup.boolean(),
      vvip: Yup.boolean(),
      normal: Yup.boolean(),
      isBulk: Yup.boolean(),
      rotationSchemaReferredBys: Yup.array()
        .of(
          Yup.object().shape({
            userId: Yup.string().required("User ID is required"),
            fullName: Yup.string().required("Full name is required"),
          })
        )
        .min(1, translate("error-refer-by-required")),
    })
      .test(
        "at-least-one-lead-category",
        "At least one lead category must be selected",
        function (value) {
          const { vip, vvip, normal } = value || {};
          if (vip || vvip || normal) return true;
          return this.createError({
            path: "vip", // attaches the error to vip to show error
            message: translate("error-lead-class-not-selected"),
          });
        }
      )
      .test(
        "at-least-one-lead-type",
        "At least one lead type must be selected",
        function (value) {
          const { buyer, tenant } = value || {};
          if (buyer || tenant) return true;
          return this.createError({
            path: "buyer",
            message: translate("error-lead-type-not-selected"),
          });
        }
      );
  };
  const { translate } = useTranslate("NewAgentsRotationCriteria");
  const {
    theme: { palette },
  } = useSelectedTheme();
  const styles = useStyles();

  return (
    <Formik
      innerRef={formRef}
      initialValues={{ ...schemaDetails }}
      validationSchema={getValidationSchema()}
      validateOnChange
    >
      {({ values, setFieldValue, errors, handleChange }) => (
        <Form>
          <Box className={styles.formContent}>
            <Box>
              <CustomCard
                boxShadow="xs"
                borderRadius="xl"
                borderColor="secondary"
                classes={styles.cardContainer}
              >
                <ReskinningInputs
                  name="label"
                  idRef="label"
                  labelValue={translate("rotation-label")}
                  value={values.label}
                  inputPlaceholder={translate("enter")}
                  fieldClasses={styles.inputwrapper}
                  rootCustomClasses={styles.inputwrapper}
                  onInputChanged={handleChange}
                  isWithError={!!errors.label}
                  error={!!errors.label}
                  helperText={errors.label}
                  forceError={errors.label}
                />
                <ReskinningInputs
                  name="order"
                  idRef="order"
                  labelValue={translate("schema-order-label")}
                  type="number"
                  value={values.order}
                  inputPlaceholder={translate("enter")}
                  fieldClasses={styles.inputwrapper}
                  rootCustomClasses={styles.inputwrapper}
                  onInputChanged={handleChange}
                  isWithError={!!errors.order}
                  error={!!errors.order}
                  helperText={errors.order}
                  forceError={errors.order}
                />
                <Box className={styles.fieldWrapper}>
                  <span className={styles.label}>{translate("active")}</span>
                  <Box className={styles.switchWrapper}>
                    <CustomSwitch
                      name="isActive"
                      size={"md"}
                      checked={values.isActive}
                      onChange={(e) => {
                        setFieldValue("isActive", Number(e.target.checked));
                      }}
                    />
                  </Box>
                </Box>
                <CustomAsyncAutocomplete
                  labelValue={translate("media-detail-label")}
                  autocompleteClasses={styles.optionalField}
                  multiple
                  selectedValues={values.rotationSchemeMedias}
                  displayLabel={(option) => option?.mediaDetails ?? ""}
                  renderOption={(option) => {
                    return option?.mediaDetailsName;
                  }}
                  chipsLabel={(option) => option?.mediaDetails}
                  getOptionSelected={(option, value) =>
                    option?.mediaDetailsId === value?.mediaDetailsId
                  }
                  filter={{
                    lookupTypeId: StaticLookupsIds.MediaDetail,
                    pageIndex: 1,
                    pageSize: 10000,
                  }}
                  onFetchData={lookupItemsGetId}
                  mapper={(data) =>
                    data.map(({ lookupItemName, lookupItemId }) => ({
                      mediaDetailsId: lookupItemId,
                      mediaDetailsName: lookupItemName,
                    }))
                  }
                  onChange={(_e, value) =>
                    setFieldValue("rotationSchemeMedias", value)
                  }
                  rootCustomClasses={styles.inputwrapper}
                  withoutSearchButton
                />
                <CustomAsyncAutocomplete
                  labelValue={translate("lead-campaign-label")}
                  autocompleteClasses={styles.optionalField}
                  withoutSearchButton
                  lookupTypeId={StaticLookupsIds.CampaignTypes}
                  selectedValues={values.rotationSchemeCampaigns}
                  filter={{
                    pageSize: 50,
                    pageIndex: 0,
                    propertyId: null,
                    eventTypeId: null,
                    countryId: null,
                    cityId: null,
                    propertyCampaignName: "",
                  }}
                  mapper={(data) => {
                    return data.map((item) => ({
                      name: item.name,
                      campaignName: item.campaignName ?? "",
                      propertyCampaignId: item.propertyCampaignId,
                    }));
                  }}
                  queryKey="propertyCampaignName"
                  displayLabel={(option) => option?.name ?? ""}
                  renderOption={(option) => option?.name}
                  chipsLabel={(option) => {
                    return option?.name || option.campaignName;
                  }}
                  getOptionSelected={(option, value) =>
                    option?.propertyCampaignId === value?.propertyCampaignId
                  }
                  onFetchData={GetAllCampaigns}
                  onChange={(_e, value) => {
                    setFieldValue("rotationSchemeCampaigns", value);
                  }}
                  rootCustomClasses={styles.inputwrapper}
                  refetchOnSearch
                  multiple
                />
                <Box className={styles.fieldWrapper}>
                  <label className={styles.label}>
                    {translate("lead-type-label")}
                  </label>
                  <Box>
                    <Box className={styles.checkboxWrapper}>
                      <CustomCheckbox
                        name="buyer"
                        checked={values.buyer}
                        onChange={handleChange}
                      />
                      <span className={styles.checkboxLabel}>
                        {translate("buyer")}
                      </span>
                      <CustomCheckbox
                        name="tenant"
                        checked={values.tenant}
                        onChange={handleChange}
                      />
                      <span className={styles.checkboxLabel}>
                        {translate("tenant")}
                      </span>
                    </Box>
                    {errors?.buyer && (
                      <div className={styles.errorMessage}>{errors.buyer}</div>
                    )}
                  </Box>
                </Box>
                <CustomAsyncAutocomplete
                  labelValue={translate("method-of-contacts-label")}
                  autocompleteClasses={styles.optionalField}
                  selectedValues={values.rotationSchemaMethodOfContacts.map(
                    ({ methodOfContactName, methodOfContactId }) => ({
                      lookupItemName: methodOfContactName,
                      lookupItemId: methodOfContactId,
                    })
                  )}
                  multiple
                  displayLabel={(option) => option?.lookupItemName ?? ""}
                  renderOption={(option) => option?.lookupItemName}
                  chipsLabel={(option) => option?.lookupItemName}
                  getOptionSelected={(option, value) =>
                    option?.lookupItemId === value?.lookupItemId
                  }
                  onFetchData={lookupItemsGetId}
                  filter={{
                    lookupTypeId: StaticLookupsIds.MethodOfContact,
                    pageIndex: 1,
                    pageSize: 10000,
                  }}
                  mapper={(data) => data}
                  onChange={(_e, value) => {
                    setFieldValue("rotationSchemaMethodOfContacts", value);
                  }}
                  withoutSearchButton
                  rootCustomClasses={styles.inputwrapper}
                />
                <Box className={styles.fieldWrapper}>
                  <span className={styles.label}>
                    {translate("lead-class-label")}
                  </span>
                  <Box className={styles.switchWrapper}>
                    <CustomSwitch
                      label={translate("normal")}
                      name="normal"
                      size={"md"}
                      checked={values.normal}
                      onChange={handleChange}
                    />
                    <CustomSwitch
                      label={translate("vip")}
                      name="vip"
                      size={"md"}
                      checked={values.vip}
                      onChange={handleChange}
                    />

                    <CustomSwitch
                      label={translate("vvip")}
                      name="vvip"
                      size={"md"}
                      checked={values.vvip}
                      onChange={handleChange}
                    />
                    {errors?.vip && (
                      <div className={styles.errorMessage}>{errors.vip}</div>
                    )}
                  </Box>
                </Box>
                <CustomAsyncAutocomplete
                  labelValue={translate("lead-preferred-language-label")}
                  multiple
                  selectedValues={values.rotationPreferredLanguages}
                  displayLabel={(option) => option?.lookupItemName ?? ""}
                  renderOption={(option) => option?.lookupItemName}
                  chipsLabel={(option) => option?.lookupItemName}
                  getOptionSelected={(option, value) =>
                    option?.languageId === value?.languageId
                  }
                  onFetchData={lookupItemsGetId}
                  filter={{
                    lookupTypeId: StaticLookupsIds.Languages,
                    pageIndex: 1,
                    pageSize: 10000,
                  }}
                  mapper={(data) =>
                    data.map(({ lookupItemId, lookupItemName }) => ({
                      languageId: lookupItemId,
                      lookupItemName,
                    }))
                  }
                  onChange={(_e, value) => {
                    setFieldValue("rotationPreferredLanguages", value);
                  }}
                  rootCustomClasses={styles.inputwrapper}
                  withoutSearchButton
                />
                <CustomAsyncAutocomplete
                  labelValue={translate("country")}
                  autocompleteClasses={styles.optionalField}
                  placeholder={translate("location-country")}
                  multiple
                  selectedValues={values.rotationSchemeCountries}
                  onFetchData={lookupItemsGetId}
                  displayLabel={(option) => option?.lookupItemName ?? ""}
                  renderOption={(option) => option?.lookupItemName}
                  chipsLabel={(option) => option?.lookupItemName}
                  getOptionSelected={(option, value) =>
                    option?.lookupsId === value?.lookupsId
                  }
                  filter={{
                    lookupTypeId: StaticLookupsIds.Country,
                    pageIndex: 1,
                    pageSize: 10000,
                  }}
                  mapper={(data) => data}
                  onChange={(_e, value) => {
                    setFieldValue("rotationSchemeCountries", value);
                  }}
                  rootCustomClasses={styles.inputwrapper}
                  withoutSearchButton
                />
                <CustomAsyncAutocomplete
                  labelValue={translate("city")}
                  autocompleteClasses={styles.optionalField}
                  placeholder={translate("location-city")}
                  multiple
                  selectedValues={values.rotationSchemeCities}
                  onFetchData={lookupItemsGetId}
                  displayLabel={(option) => option?.lookupItemName ?? ""}
                  renderOption={(option) => option?.lookupItemName}
                  chipsLabel={(option) => option?.lookupItemName}
                  getOptionSelected={(option, value) =>
                    option?.lookupsId === value?.lookupsId
                  }
                  filter={{
                    lookupTypeId: StaticLookupsIds.Cities,
                    pageIndex: 1,
                    pageSize: 10000,
                  }}
                  onChange={(_e, value) => {
                    setFieldValue("rotationSchemeCities", value);
                  }}
                  mapper={(data) => data}
                  rootCustomClasses={styles.inputwrapper}
                  withoutSearchButton
                />
                <CustomAsyncAutocomplete
                  labelValue={translate("district")}
                  autocompleteClasses={styles.optionalField}
                  placeholder={translate("location-distric")}
                  multiple
                  selectedValues={values.rotationSchemeDistricts}
                  onFetchData={lookupItemsGetId}
                  displayLabel={(option) => option?.lookupItemName ?? ""}
                  renderOption={(option) => option?.lookupItemName}
                  chipsLabel={(option) => option?.lookupItemName}
                  getOptionSelected={(option, value) =>
                    option?.lookupsId === value?.lookupsId
                  }
                  filter={{
                    lookupTypeId: StaticLookupsIds.District,
                    pageIndex: 1,
                    pageSize: 10000,
                  }}
                  mapper={(data) => data}
                  rootCustomClasses={styles.inputwrapper}
                  onChange={(_e, value) => {
                    setFieldValue("rotationSchemeDistricts", value);
                  }}
                  withoutSearchButton
                />
                <CustomAsyncAutocomplete
                  labelValue={translate("community")}
                  autocompleteClasses={styles.optionalField}
                  placeholder={translate("location-community")}
                  multiple
                  onFetchData={lookupItemsGetId}
                  selectedValues={values.rotationSchemeCommunities}
                  displayLabel={(option) => option?.lookupItemName ?? ""}
                  renderOption={(option) => option?.lookupItemName}
                  chipsLabel={(option) => option?.lookupItemName}
                  getOptionSelected={(option, value) =>
                    option?.lookupsId === value?.lookupsId
                  }
                  filter={{
                    lookupTypeId: StaticLookupsIds.Community,
                    pageIndex: 1,
                    pageSize: 10000,
                  }}
                  mapper={(data) => data}
                  rootCustomClasses={styles.inputwrapper}
                  onChange={(_e, value) => {
                    setFieldValue("rotationSchemeCommunities", value);
                  }}
                  withoutSearchButton
                />
                <CustomAsyncAutocomplete
                  labelValue={translate("sub-community")}
                  autocompleteClasses={styles.optionalField}
                  placeholder={translate("location-sub-community")}
                  multiple
                  selectedValues={values.rotationSchemeSubCommunities}
                  onFetchData={lookupItemsGetId}
                  displayLabel={(option) => option?.lookupItemName ?? ""}
                  renderOption={(option) => option?.lookupItemName}
                  chipsLabel={(option) => option?.lookupItemName}
                  getOptionSelected={(option, value) =>
                    option?.lookupsId === value?.lookupsId
                  }
                  filter={{
                    lookupTypeId: StaticLookupsIds.SubCommunity,
                    pageIndex: 1,
                    pageSize: 10000,
                  }}
                  mapper={(data) => data}
                  rootCustomClasses={styles.inputwrapper}
                  onChange={(_e, value) => {
                    setFieldValue("rotationSchemeSubCommunities", value);
                  }}
                  withoutSearchButton
                />
                <CustomAsyncAutocomplete
                  rootCustomClasses={styles.inputwrapper}
                  autocompleteClasses={styles.optionalField}
                  selectedValues={values.rotationSchemeProperties}
                  multiple
                  labelValue={translate("properties")}
                  placeholder={translate("properties-placeholder")}
                  mapper={(data) => {
                    return data.map((item) => ({
                      propertyId: item.propertyId,
                      propertyName:
                        item.property && item.property.property_name,
                      property_plan:
                        item.property &&
                        item.property.property_plan &&
                        item.property.property_plan.lookupItemName,
                    }));
                  }}
                  queryKey={"search"}
                  onFetchData={getProperties}
                  filter={{ pageIndex: 0, pageSize: 50 }}
                  displayLabel={(option) => option?.propertyName ?? ""}
                  renderOption={(option) => option?.propertyName}
                  chipsLabel={(option) => {
                    return option?.propertyName;
                  }}
                  getOptionSelected={(option, value) =>
                    option?.propertyId === value?.propertyId
                  }
                  onChange={(_e, value) => {
                    setFieldValue("rotationSchemeProperties", value);
                  }}
                  refetchOnSearch
                />
                <CustomAsyncAutocomplete
                  labelValue={translate("developer-label")}
                  rootCustomClasses={styles.inputwrapper}
                  autocompleteClasses={styles.optionalField}
                  multiple
                  selectedValues={values.developer}
                  onChange={(_e, value) => {
                    setFieldValue("developer", value);
                  }}
                  mapper={(data) => {
                    return data.map((item) => {
                      const developerContact =
                        item?.contact && JSON.parse(item.contact);
                      return {
                        developerId: item.contactsId,
                        developerName: developerContact?.contact?.company_name,
                      };
                    });
                  }}
                  queryKey={"search"}
                  onFetchData={GetAllDevelopersContactsAPI}
                  filter={{ pageIndex: 1, pageSize: 50 }}
                  displayLabel={(option) => option?.developerName ?? ""}
                  renderOption={(option) => option?.developerName}
                  chipsLabel={(option) => {
                    return option?.developerName;
                  }}
                  getOptionSelected={(option, value) =>
                    option?.developerId === value?.developerId
                  }
                  refetchOnSearch
                />
                <CustomAsyncAutocomplete
                  labelValue={translate("referred-by-label")}
                  rootCustomClasses={styles.inputwrapper}
                  multiple
                  selectedValues={values.rotationSchemaReferredBys}
                  onChange={(_e, value) => {
                    setFieldValue("rotationSchemaReferredBys", value);
                  }}
                  mapper={(data) => {
                    return data.map((item) => ({
                      userId: item.id,
                      fullName: item.fullName,
                    }));
                  }}
                  queryKey={"name"}
                  onFetchData={OrganizationUserSearch}
                  filter={{ pageIndex: 0, pageSize: 50, userStatusId: 2 }}
                  displayLabel={(option) => option?.fullName ?? ""}
                  renderOption={(option) => option?.fullName}
                  chipsLabel={(option) => {
                    return option?.fullName;
                  }}
                  getOptionSelected={(option, value) =>
                    option?.userId === value?.userId
                  }
                  refetchOnSearch
                  error={Boolean(errors.rotationSchemaReferredBys)}
                  withError={Boolean(errors.rotationSchemaReferredBys)}
                  helperText={errors.rotationSchemaReferredBys}
                  forceError={errors.rotationSchemaReferredBys}
                />
                <CustomAsyncAutocomplete
                  labelValue={translate("unit-type-label")}
                  autocompleteClasses={styles.optionalField}
                  multiple
                  displayLabel={(option) => option?.unitTypeName ?? ""}
                  renderOption={(option) => option?.unitTypeName}
                  chipsLabel={(option) => option?.unitTypeName}
                  getOptionSelected={(option, value) =>
                    option?.unitTypeId === value?.unitTypeId
                  }
                  selectedValues={values.rotationSchemeUnitTypes.map(
                    ({ lookupItemName, lookupsId }) => ({
                      unitTypeName: lookupItemName,
                      unitTypeId: lookupsId,
                    })
                  )}
                  onChange={(_e, value) => {
                    setFieldValue("rotationSchemeUnitTypes", value);
                  }}
                  onFetchData={lookupItemsGetId}
                  filter={{
                    lookupTypeId: StaticLookupsIds.UintType,
                    pageIndex: 1,
                    pageSize: 10000,
                  }}
                  mapper={(data) =>
                    data.map(({ lookupItemName, lookupItemId }) => ({
                      unitTypeId: lookupItemId,
                      unitTypeName: lookupItemName,
                    }))
                  }
                  withoutSearchButton
                  rootCustomClasses={styles.inputwrapper}
                />
                <Box className={styles.fieldWrapper}>
                  <span className={styles.label}>{translate("isBulk")}</span>
                  <Box className={styles.isBulkContainer}>
                    <ReskinningRadioGroup
                      rootCustomClasses={styles.radioRootWrapper}
                      data={[
                        {
                          value: "true",
                          label: translate("yes"),
                        },
                        {
                          value: "false",
                          label: translate("no"),
                        },
                      ]}
                      onSelectedRadioChanged={(e) => {
                        setFieldValue("isBulk", e.target.value);
                      }}
                      value={String(values.isBulk)}
                      name="Active"
                      labelInput="label"
                      valueInput="value"
                      themeClass="theme-line"
                    />
                  </Box>
                </Box>
                <Box className={styles.fieldWrapper}>
                  <span className={styles.label}>
                    {translate("Price Range")}
                  </span>
                  <Box className={styles.rangeSlider}>
                    <CustomRangeSlider
                      value={values.priceRange}
                      handleChange={(e, newValue) => {
                        if (newValue?.[0] || newValue?.[1])
                          if (newValue?.[0] > newValue?.[1]) return;
                        setFieldValue("priceRange", [
                          newValue?.[0],
                          newValue?.[1],
                        ]);
                      }}
                      min={0}
                      max={600000}
                      valueLabelDisplay="off"
                      preInputText={"AED"}
                    />
                  </Box>
                </Box>
                <Box className={styles.fieldWrapper}>
                  <span className={styles.label}>
                    {translate("size-label")}
                  </span>
                  <Box className={styles.rangeSlider}>
                    <CustomRangeSlider
                      value={values.size}
                      handleChange={(e, newValue) => {
                        if (newValue?.[0] || newValue?.[1])
                          if (newValue?.[0] > newValue?.[1]) return;
                        setFieldValue("size", [newValue?.[0], newValue?.[1]]);
                      }}
                      min={0}
                      max={10000}
                      valueLabelDisplay="off"
                      preInputText={"Ft"}
                    />
                  </Box>
                </Box>
                <Box className={styles.fieldWrapper}>
                  <span className={styles.label}>
                    {translate("bedroom-label")}
                  </span>
                  <Box className={styles.rangeSlider}>
                    <CustomRangeSlider
                      value={values.bedrooms}
                      handleChange={(e, newValue) => {
                        if (newValue?.[0] || newValue?.[1])
                          if (newValue?.[0] > newValue?.[1]) return;
                        setFieldValue("bedrooms", [
                          newValue?.[0],
                          newValue?.[1],
                        ]);
                      }}
                      min={0}
                      max={8}
                      valueLabelDisplay="off"
                    />
                  </Box>
                </Box>
                <Box className={styles.fieldWrapper}>
                  <span className={styles.label}>
                    {translate("bathroom-label")}
                  </span>
                  <Box className={styles.rangeSlider}>
                    <CustomRangeSlider
                      value={values.bathrooms}
                      handleChange={(e, newValue) => {
                        if (newValue?.[0] || newValue?.[1])
                          if (newValue?.[0] > newValue?.[1]) return;
                        setFieldValue("bathrooms", [
                          newValue?.[0],
                          newValue?.[1],
                        ]);
                      }}
                      min={0}
                      max={8}
                      valueLabelDisplay="off"
                    />
                  </Box>
                </Box>
              </CustomCard>
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  );
}

export default EditSchemaForm;
